import window from '@atlas/tool/box/browser/window';

/**
 * Do not use it in React dependencies because its reference is never changed
 */
const lastCapturedKeyDowns: [lastEvent?: KeyboardEvent, previousEvent?: KeyboardEvent] = [undefined, undefined];

export default lastCapturedKeyDowns;

// #region Helpers

const historyLimit = lastCapturedKeyDowns.length; // Should be set to the items that we need to store in history

function updateLastCapturedKeyDowns(event: KeyboardEvent) {
    lastCapturedKeyDowns.unshift(event);
    lastCapturedKeyDowns.splice(historyLimit);
}

window && window.document.documentElement.addEventListener('keydown', updateLastCapturedKeyDowns, { capture: true });

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, vi, expect, beforeAll, afterEach, afterAll } = import.meta.vitest;

    describe('lastCapturedKeyDowns', () => {
        beforeAll(function mockAddEventListenerOnDocumentElement() {
            vi.mock('@atlas/tool/box/browser/window', () => {
                const listeners = [] as Array<(event: KeyboardEvent) => void>;
                return {
                    default: {
                        document: {
                            documentElement: {
                                addEventListener: vi.fn((_type: string, listener: (event: KeyboardEvent) => void) => {
                                    listeners.push(listener);
                                }),
                            },
                        },
                        _listeners: listeners,
                        _fire(event: KeyboardEvent) {
                            listeners.forEach((listener) => listener(event));
                        },
                    },
                };
            });
        });

        afterEach(function resetState() {
            lastCapturedKeyDowns.splice(0);
            vi.resetAllMocks();
        });

        afterAll(function removeMocks() {
            // vi.unmock('@atlas/tool/box/browser/window');
        });

        function fireKeydown<T extends KeyboardEvent>(event: T) {
            (window as unknown as { _fire: (event: T) => void })._fire(event);
        }

        it('has subscribed to keydown event', () => {
            if (!window) return;
            expect(window.document.documentElement.addEventListener).toBeCalledTimes(1);
            expect(window.document.documentElement.addEventListener).toBeCalledWith(
                'keydown',
                updateLastCapturedKeyDowns,
                { capture: true },
            );
        });

        it('keeps last 2 keydown events in the state', () => {
            // Keyboard events are not well compared by vitest

            expect(lastCapturedKeyDowns).toHaveLength(0);

            const firstEvent = new KeyboardEvent('keydown', { which: 65 });
            fireKeydown(firstEvent);
            expect(lastCapturedKeyDowns).toHaveLength(1);
            expect(lastCapturedKeyDowns[0]).toBe(firstEvent);

            const secondEvent = new KeyboardEvent('keydown', { which: 66 });
            fireKeydown(secondEvent);
            expect(lastCapturedKeyDowns).toHaveLength(2);
            expect(lastCapturedKeyDowns[0]).toBe(secondEvent);
            expect(lastCapturedKeyDowns[1]).toBe(firstEvent);

            const thirdEvent = new KeyboardEvent('keydown', { which: 67 });
            fireKeydown(thirdEvent);
            expect(lastCapturedKeyDowns).toHaveLength(2);
            expect(lastCapturedKeyDowns[0]).toBe(thirdEvent);
            expect(lastCapturedKeyDowns[1]).toBe(secondEvent);

            const fourthEvent = new KeyboardEvent('keydown', { which: 68 });
            fireKeydown(fourthEvent);
            expect(lastCapturedKeyDowns).toHaveLength(2);
            expect(lastCapturedKeyDowns[0]).toBe(fourthEvent);
            expect(lastCapturedKeyDowns[1]).toBe(thirdEvent);
        });
    });
}

// #endregion
